// src/components/AdminPanelPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, X } from 'lucide-react';
import { api } from '../utils/client';
import { useCaps } from '../contexts/CapabilitiesContext';
import AdminOrganizationsTab from './admin/organizations/AdminOrganizationsTab';
import AdminMarketplaceTab from './admin/marketplace/AdminMarketplaceTab';
import AdminMiscTab from './admin/misc/AdminMiscTab';
import AdminOrdersTab from './admin/orders/AdminOrdersTab';
import AdminRolesCapabilitiesTab from './admin/roles/AdminRolesCapabilitiesTab';
import AdminUsersTab from './admin/users/AdminUsersTab';
import AdminAiPromptsTab from './admin/ai_prompts/AdminAiPromptsTab';
import { AI_PROMPTS_SUBTAB_CAPS } from './admin/ai_prompts/types';
import { MARKETPLACE_SUBTAB_CAPS } from './admin/marketplace/types';
import { MISC_SUBTAB_CAPS } from './admin/misc/types';
import { ORDERS_SUBTAB_CAPS } from './admin/orders/types';
import { ORGS_SUBTAB_CAPS } from './admin/organizations/types';
import { ROLES_SUBTAB_CAPS } from './admin/roles/types';
import { TIERS_SUBTAB_CAPS } from './admin/tiers/types';
import { USERS_SUBTAB_CAPS } from './admin/users/types';
import {
  AdminSubtabConfig,
  AdminSubtabsProvider,
} from './admin/subtabs';

interface AdminTab {
  id: number;
  key: string;
  label: string;
  display_order: number;
  is_visible: boolean;
}

interface AdminPanelPageProps {
  onClose: () => void;
}

const TAB_COMPONENTS: Record<string, React.FC> = {
  users: AdminUsersTab,
  organizations: AdminOrganizationsTab,
  roles_capabilities: AdminRolesCapabilitiesTab,
  marketplace: AdminMarketplaceTab,
  products: AdminMarketplaceTab, // legacy key fallback
  orders: AdminOrdersTab,
  misc: AdminMiscTab,
  ai_prompts: AdminAiPromptsTab,
};

const TAB_CAPABILITIES: Record<string, string[]> = {
  users: Object.values(USERS_SUBTAB_CAPS),
  organizations: [...Object.values(ORGS_SUBTAB_CAPS), ...Object.values(TIERS_SUBTAB_CAPS)],
  roles_capabilities: Object.values(ROLES_SUBTAB_CAPS),
  marketplace: Object.values(MARKETPLACE_SUBTAB_CAPS),
  products: Object.values(MARKETPLACE_SUBTAB_CAPS),
  orders: Object.values(ORDERS_SUBTAB_CAPS),
  misc: Object.values(MISC_SUBTAB_CAPS),
  ai_prompts: Object.values(AI_PROMPTS_SUBTAB_CAPS),
};

const HIDDEN_TAB_KEYS = new Set(['tiers', 'prompts']);

const AdminPanelPage: React.FC<AdminPanelPageProps> = ({ onClose }) => {
  const { can, loading: capsLoading, allCapabilities } = useCaps();
  const [tabs, setTabs] = useState<AdminTab[]>([]);
  const [subtabs, setSubtabs] = useState<AdminSubtabConfig[]>([]);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTabs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [visible, allSubtabs] = await Promise.all([
        api<AdminTab[] | { results: AdminTab[] }>('/api/admin/tabs/'),
        api<AdminSubtabConfig[] | { results: AdminSubtabConfig[] }>(
          '/api/admin/subtabs/?all=1'
        ),
      ]);
      const visibleList = Array.isArray(visible) ? visible : visible?.results ?? [];
      const subtabList = Array.isArray(allSubtabs)
        ? allSubtabs
        : allSubtabs?.results ?? [];
      setTabs(visibleList);
      setSubtabs(subtabList);
      setActiveKey((current) => {
        if (current && visibleList.some((t) => t.key === current)) return current;
        return visibleList[0]?.key ?? null;
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load admin tabs');
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshSubtabs = useCallback(async () => {
    try {
      const allSubtabs = await api<
        AdminSubtabConfig[] | { results: AdminSubtabConfig[] }
      >('/api/admin/subtabs/?all=1');
      setSubtabs(Array.isArray(allSubtabs) ? allSubtabs : allSubtabs?.results ?? []);
      // Also refresh visible tabs so reorder/visibility changes show up
      const visible = await api<AdminTab[] | { results: AdminTab[] }>('/api/admin/tabs/');
      setTabs(Array.isArray(visible) ? visible : visible?.results ?? []);
    } catch {
      // Keep existing state on refresh failure
    }
  }, []);

  useEffect(() => {
    void loadTabs();
  }, [loadTabs]);

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      if (HIDDEN_TAB_KEYS.has(tab.key)) return false;
      const keys = TAB_CAPABILITIES[tab.key];
      if (!keys || keys.length === 0) return true;
      if (capsLoading && !allCapabilities) return false;
      return keys.some((key) => can(key));
    });
  }, [tabs, can, capsLoading, allCapabilities]);

  useEffect(() => {
    setActiveKey((current) => {
      if (current && visibleTabs.some((t) => t.key === current)) return current;
      return visibleTabs[0]?.key ?? null;
    });
  }, [visibleTabs]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const ActiveComponent = useMemo(() => {
    if (!activeKey) return null;
    return TAB_COMPONENTS[activeKey] ?? null;
  }, [activeKey]);

  return (
    <div
      className="fixed inset-0 z-[15400] bg-black/45 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Admin Panel"
    >
      <div
        className="w-full h-full max-w-[1400px] max-h-[92vh] bg-white rounded-xl shadow-2xl font-[Inter,sans-serif] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 sm:px-8 pt-6 pb-4 border-b border-[#E8E8E8] flex-shrink-0">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-[28px] leading-none font-bold text-[#0C0B0B]">Admin Panel</h1>
              <p className="mt-2 text-[14px] text-[#626262]">
                Manage users, organizations, roles, marketplace, orders, platform settings, and AI
                prompts.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#D1D1D1] text-[#4A4A4A] hover:bg-[#F5F5F5]"
              aria-label="Close admin panel"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveKey(tab.key)}
                className={`h-9 px-4 rounded-full text-sm font-medium transition-colors ${
                  activeKey === tab.key
                    ? 'bg-[#0C0B0B] text-white'
                    : 'bg-[#F3F3F3] text-[#4A4A4A] hover:bg-[#E8E8E8]'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="px-6 sm:px-8 py-6 flex-1 overflow-y-auto">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-[#8A8A8A]">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          )}
          {error && !loading && (
            <div className="rounded-md bg-red-50 text-red-700 px-3 py-2 text-sm">{error}</div>
          )}
          {!loading && !error && ActiveComponent && (
            <AdminSubtabsProvider subtabs={subtabs} refresh={refreshSubtabs}>
              <ActiveComponent />
            </AdminSubtabsProvider>
          )}
          {!loading && !error && !ActiveComponent && (
            <div className="text-sm text-[#8A8A8A]">
              {activeKey
                ? `No UI registered for tab "${activeKey}".`
                : 'No visible tabs. Use Misc → Admin Panel Tabs Ordering to show one.'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminPanelPage;
